import { z } from 'zod';

import { businessVerificationStatusSchema } from './business';

/**
 * Official responses and the resolution timeline (business spec §7).
 *
 * These ride along on the post rather than being fetched separately: they
 * render pinned under the post body, above the comments, so the page cannot
 * draw itself without them.
 */
export const responseBusinessSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  verificationStatus: businessVerificationStatusSchema,
  logoMediaId: z.string().nullable(),
});

export const businessResponseSchema = z.object({
  id: z.string(),
  postId: z.string(),
  business: responseBusinessSchema,
  body: z.string(),
  isEdited: z.boolean(),
  createdAt: z.string(),
  editedAt: z.string().nullable(),
  canManage: z.boolean(),
});

export const resolutionStateSchema = z.enum([
  'ACKNOWLEDGED',
  'IN_PROGRESS',
  'RESOLVED',
  'WONT_FIX',
]);

export const resolutionSchema = z.object({
  id: z.string(),
  state: resolutionStateSchema,
  note: z.string().nullable(),
  businessSlug: z.string(),
  businessName: z.string(),
  linkedIdeal: z
    .object({ id: z.string(), title: z.string().nullable(), body: z.string() })
    .nullable(),
  evidenceMediaId: z.string().nullable(),
  isConfirmed: z.boolean(),
  confirmedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const responseGroundSchema = z.enum(['MENTIONED', 'SAME_TOWN', 'WATCH_TERM']);

export const responseBlockerSchema = z.enum([
  'POST_UNAVAILABLE',
  'NOT_A_MEMBER',
  'BUSINESS_SUSPENDED',
  'NOT_VERIFIED',
  'RESPONSES_SUSPENDED',
  'UNRELATED',
  'DAILY_LIMIT',
]);

export const responseEligibilitySchema = z.object({
  businessId: z.string(),
  slug: z.string(),
  name: z.string(),
  canRespond: z.boolean(),
  grounds: z.array(responseGroundSchema),
  blocker: responseBlockerSchema.nullable(),
  // The API writes this sentence, so a new blocker never arrives as a blank
  // space in the interface.
  reason: z.string(),
  existingResponseId: z.string().nullable(),
});

export const responseEligibilityListSchema = z.array(responseEligibilitySchema);
export const resolutionListSchema = z.array(resolutionSchema);

export type BusinessResponse = z.infer<typeof businessResponseSchema>;
export type Resolution = z.infer<typeof resolutionSchema>;
export type ResolutionState = z.infer<typeof resolutionStateSchema>;
export type ResponseEligibility = z.infer<typeof responseEligibilitySchema>;

export const answeredPostSchema = z.object({
  id: z.string(),
  body: z.string(),
  isEdited: z.boolean(),
  createdAt: z.string(),
  post: z.object({
    id: z.string(),
    type: z.enum(['GOOD', 'BAD', 'IDEAL']),
    title: z.string().nullable(),
    excerpt: z.string(),
    town: z.string().nullable(),
    countryCode: z.string().nullable(),
    createdAt: z.string(),
  }),
  resolution: z.object({ state: resolutionStateSchema, isConfirmed: z.boolean() }).nullable(),
});

export const answeredPostListSchema = z.array(answeredPostSchema);

export type AnsweredPost = z.infer<typeof answeredPostSchema>;
